import React from 'react';
import { resolveCompanies } from './resolveCompanies';

// The company textarea, and turning what is in it into companies.
//
// /feed/view takes numeric company_ids, so a pasted list of tickers has to be resolved before it can
// be sent — one request per symbol, at concurrency 6, against the ticker search. That fan-out lived
// in FeedViewForm, which made a form component the owner of a network pipeline, a progress meter and
// a cancel token: move the company filter into a modal, a sidebar or a persistent toolbar and all of
// it came too, because none of it lived in CompanyFilter.
//
// `flush()` returns the list the run should use, in the same tick. That is the awkward part and it
// is real: the parent owns `companies`, so at the moment of submit the parent's copy is still the
// pre-resolve one. Returning the merged list is what lets the caller send the right one without
// waiting a render.
//
// THE CANCEL TOKEN IS A STALE-SETSTATE GUARD, NOT A CORRECTNESS HOLE. It flips only on unmount, so
// it does not cancel the in-flight requests — it stops the writes that would land after the form has
// gone. Worth saying because it looks like cancellation and is not, and "fixing" it into
// cancellation would change what a submit does.
export const useCompanyResolution = ({ companies, onCompaniesChange }) => {
  const [text, setText] = React.useState('');
  const [isResolving, setIsResolving] = React.useState(false);
  const [progress, setProgress] = React.useState({ done: 0, total: 0 });
  const [result, setResult] = React.useState(null);

  const cancelRef = React.useRef({ isCanceled: false });
  React.useEffect(() => {
    const token = cancelRef.current;
    return () => {
      token.isCanceled = true;
    };
  }, []);

  // Returns the company list the run should use, so the caller has it in the same tick.
  const flush = React.useCallback(async () => {
    if (!text.trim()) return companies;
    setIsResolving(true);
    setProgress({ done: 0, total: 0 });
    const outcome = await resolveCompanies(text, companies, {
      onProgress: (done, total) => {
        if (!cancelRef.current.isCanceled) setProgress({ done, total });
      },
    });
    if (cancelRef.current.isCanceled) return companies;
    setIsResolving(false);
    setResult(outcome);
    // Only the failures stay behind, so they can be corrected in place instead of retyped.
    setText(outcome.remainingText);
    const next = [...companies, ...outcome.added];
    if (outcome.added.length) onCompaniesChange(next);
    return next;
  }, [text, companies, onCompaniesChange]);

  const dismiss = React.useCallback(
    (entry) =>
      setResult((prev) =>
        prev ? { ...prev, unresolved: prev.unresolved.filter((item) => item.entry !== entry) } : prev
      ),
    []
  );

  // Whether a submit has to wait. The caller asks rather than inspecting `text`, so "is there
  // pending work" stays one question with one answer.
  const hasPendingText = !!text.trim();

  // Memoized, and it is load-bearing rather than tidy. This object goes straight into
  // FeedViewContext's value; returned fresh each render it invalidated that memo unconditionally, so
  // EVERY consumer re-rendered on every keystroke — defeating the thing the value memo exists for.
  return React.useMemo(
    () => ({ text, setText, isResolving, progress, result, dismiss, flush, hasPendingText }),
    [text, isResolving, progress, result, dismiss, flush, hasPendingText]
  );
};
